import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaUser, FaCar, FaCog, FaSignOutAlt, FaEdit, FaPhone, FaEnvelope, FaCalendarAlt, FaCamera, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import ImageUpload from '../ImageUpload';

const Container = styled.div`
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: ${theme.colors.white};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto ${theme.spacing.md};
`;

const UserName = styled.h1`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.sm};
`;

const UserEmail = styled.p`
  opacity: 0.9;
  font-size: 1rem;
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Card = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing.md};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  border-bottom: 1px solid ${theme.colors.lightGray};
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoIcon = styled.div`
  color: ${theme.colors.primary};
  font-size: 1.2rem;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.xs};
`;

const InfoValue = styled.div`
  color: ${theme.colors.mediumGray};
`;

const CarCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing.md};
  border-left: 4px solid ${theme.colors.primary};
`;

const CarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const CarTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.xs};
`;

const CarYear = styled.div`
  color: ${theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;
`;

const CarDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
`;

const CarDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const CarDetailLabel = styled.div`
  font-size: 0.8rem;
  color: ${theme.colors.mediumGray};
  text-transform: uppercase;
  font-weight: 600;
`;

const CarDetailValue = styled.div`
  font-weight: 600;
  color: ${theme.colors.secondary};
`;

const CarImages = styled.div`
  margin-top: ${theme.spacing.md};
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const ImageItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.lightGray};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ImageItem}:hover & {
    opacity: 1;
  }
`;

const ImageDeleteButton = styled.button`
  background: ${theme.colors.error};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
  }
`;

const AddImageButton = styled.button`
  background: ${theme.colors.white};
  border: 2px dashed ${theme.colors.mediumGray};
  color: ${theme.colors.mediumGray};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const EditModeButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
  }
`;

const ActionButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const LogoutButton = styled.button`
  background: ${theme.colors.error};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.mediumGray};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.lightGray};
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.secondary};
`;

const EmptyDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: ${theme.spacing.lg};
`;

const ProfilePage: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const [editingCar, setEditingCar] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
  };

  const handleEditProfile = () => {
    // Navigate to edit profile page
    console.log('Edit profile');
  };

  const handleAddCar = () => {
    // Navigate to add car page
    console.log('Add car');
  };

  const handleEditCarImages = (carId: string) => {
    setEditingCar(editingCar === carId ? null : carId);
  };

  const handleUpdateCarImages = (carId: string, photos: string[]) => {
    if (!user) return;
    
    const updatedCars = user.cars.map(car => 
      car.id === carId ? { ...car, photos } : car
    );
    
    updateUser({ cars: updatedCars });
    setEditingCar(null);
  };

  const handleRemoveImage = (carId: string, imageIndex: number) => {
    if (!user) return;
    
    const car = user.cars.find(c => c.id === carId);
    if (!car) return;
    
    const updatedPhotos = car.photos.filter((_, index) => index !== imageIndex);
    handleUpdateCarImages(carId, updatedPhotos);
  };

  if (!user) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>
            <FaUser />
          </EmptyIcon>
          <EmptyTitle>No user data</EmptyTitle>
          <EmptyDescription>
            Unable to load user information. Please try again.
          </EmptyDescription>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Avatar>
          <FaUser />
        </Avatar>
        <UserName>{user.firstName} {user.lastName}</UserName>
        <UserEmail>{user.email}</UserEmail>
      </Header>

      <Section>
        <SectionTitle>
          <FaUser />
          Personal Information
        </SectionTitle>
        <Card>
          <InfoItem>
            <InfoIcon>
              <FaEnvelope />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{user.email}</InfoValue>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <FaPhone />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>{user.phone || 'Not provided'}</InfoValue>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <FaCalendarAlt />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Member Since</InfoLabel>
              <InfoValue>March 2024</InfoValue>
            </InfoContent>
          </InfoItem>
        </Card>
        <ActionButton onClick={handleEditProfile}>
          <FaEdit />
          Edit Profile
        </ActionButton>
      </Section>

      <Section>
        <SectionTitle>
          <FaCar />
          My Cars ({user.cars.length})
        </SectionTitle>
        {user.cars.length === 0 ? (
          <Card>
            <EmptyState>
              <EmptyIcon>
                <FaCar />
              </EmptyIcon>
              <EmptyTitle>No cars added</EmptyTitle>
              <EmptyDescription>
                Add your first car to get started with our services.
              </EmptyDescription>
            </EmptyState>
            <ActionButton onClick={handleAddCar}>
              <FaCar />
              Add Your First Car
            </ActionButton>
          </Card>
        ) : (
          <>
            {user.cars.map((car: any) => (
              <CarCard key={car.id}>
                <CarHeader>
                  <div>
                    <CarTitle>{car.make} {car.model}</CarTitle>
                    <CarYear>{car.year}</CarYear>
                  </div>
                  <EditModeButton onClick={() => handleEditCarImages(car.id)}>
                    <FaCamera />
                    {editingCar === car.id ? 'Done' : 'Edit Photos'}
                  </EditModeButton>
                </CarHeader>
                <CarDetails>
                  <CarDetail>
                    <CarDetailLabel>Make</CarDetailLabel>
                    <CarDetailValue>{car.make}</CarDetailValue>
                  </CarDetail>
                  <CarDetail>
                    <CarDetailLabel>Model</CarDetailLabel>
                    <CarDetailValue>{car.model}</CarDetailValue>
                  </CarDetail>
                  <CarDetail>
                    <CarDetailLabel>Year</CarDetailLabel>
                    <CarDetailValue>{car.year}</CarDetailValue>
                  </CarDetail>
                  <CarDetail>
                    <CarDetailLabel>Specification</CarDetailLabel>
                    <CarDetailValue>{car.spec || 'Not specified'}</CarDetailValue>
                  </CarDetail>
                </CarDetails>
                
                <CarImages>
                  {car.photos && car.photos.length > 0 ? (
                    <ImageGrid>
                      {car.photos.map((photo: string, index: number) => (
                        <ImageItem key={index}>
                          <Image src={photo} alt={`${car.make} ${car.model} photo ${index + 1}`} />
                          {editingCar === car.id && (
                            <ImageOverlay>
                              <ImageDeleteButton onClick={() => handleRemoveImage(car.id, index)}>
                                <FaTrash />
                              </ImageDeleteButton>
                            </ImageOverlay>
                          )}
                        </ImageItem>
                      ))}
                      {editingCar === car.id && car.photos.length < 5 && (
                        <AddImageButton onClick={() => {/* Will be handled by ImageUpload component */}}>
                          <FaCamera />
                          <span style={{ fontSize: '0.7rem' }}>Add</span>
                        </AddImageButton>
                      )}
                    </ImageGrid>
                  ) : (
                    <div style={{ textAlign: 'center', color: theme.colors.mediumGray, padding: theme.spacing.lg }}>
                      No photos added yet
                    </div>
                  )}
                  
                  {editingCar === car.id && (
                    <div style={{ marginTop: theme.spacing.md }}>
                      <ImageUpload
                        images={car.photos || []}
                        onImagesChange={(photos) => handleUpdateCarImages(car.id, photos)}
                        maxImages={5}
                        userId={user.firstName + user.lastName}
                        carId={car.id}
                      />
                    </div>
                  )}
                </CarImages>
              </CarCard>
            ))}
            <ActionButton onClick={handleAddCar}>
              <FaCar />
              Add Another Car
            </ActionButton>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>
          <FaCog />
          Settings
        </SectionTitle>
        <Card>
          <InfoItem>
            <InfoIcon>
              <FaCog />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Notifications</InfoLabel>
              <InfoValue>Enabled</InfoValue>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <FaCog />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Language</InfoLabel>
              <InfoValue>English</InfoValue>
            </InfoContent>
          </InfoItem>
          <InfoItem>
            <InfoIcon>
              <FaCog />
            </InfoIcon>
            <InfoContent>
              <InfoLabel>Theme</InfoLabel>
              <InfoValue>Light</InfoValue>
            </InfoContent>
          </InfoItem>
        </Card>
      </Section>

      <LogoutButton onClick={handleLogout}>
        <FaSignOutAlt />
        Sign Out
      </LogoutButton>
    </Container>
  );
};

export default ProfilePage;
